import React from "react";
import { Icon } from "../core/Icon.jsx";

const TONES = { green: "--ac-green", amber: "--ac-amber", red: "--ac-red", blue: "--ac-blue" };

// Der Deckel einer Spalte (`maxWidth`) hängt nicht an der Zelle allein. Diese Tabelle
// setzt bewusst kein festes Tabellenlayout — die Spaltenbreite folgt dem Inhalt, und ein
// max-width an <th>/<td> ist in dieser Rechnung bestenfalls eine Bitte. Was zählt, ist
// die Wunschbreite dessen, was *in* der Zelle steht; deshalb hält den Deckel ein Block
// darin, und die Zelle trägt ihn zusätzlich, damit Kopf und Zelle dieselbe Kante nennen.
// Im Kopf ist dieser Block die Hülle um Text und Sortierzeichen, nicht der Text allein:
// sonst käme das Zeichen daneben noch obendrauf.
//
// Abgeschnitten wird statt umgebrochen: eine Zeilenhöhe, die je Zeile springt, ist in
// einer dichten Liste schlimmer als ein gekürzter Text. Ohne maxWidth entsteht kein
// Stil — eine Tabelle, die den Deckel nicht bestellt hat, zeichnet wie bisher.
const CLIP = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" };

// Die waagerechten Innenabstände einer Zelle; Kopf und Rumpf haben dieselben (unten am
// padding nachzulesen). Sie werden vom Deckel abgezogen, weil `maxWidth` dasselbe meinen
// muss wie `width` daneben: die Außenkante der Spalte. box-sizing ist border-box, also
// zählt an der Zelle die Außenkante — der Block, der den Deckel hält, sitzt aber
// innerhalb der Abstände und muss um sie schmaler sein. Ohne den Abzug läge die Spalte
// genau um sie daneben.
const CELL_PADDING_X = 16;
const capStyle = (maxWidth) => maxWidth == null ? undefined : {
  maxWidth: typeof maxWidth === "number" ? Math.max(0, maxWidth - CELL_PADDING_X)
    : "calc(" + maxWidth + " - " + CELL_PADDING_X + "px)",
  ...CLIP,
};
// Für den Text im Kopf: kürzen ja, ein zweiter Deckel nein — den trägt die Hülle um ihn.
const clipStyle = (maxWidth) => maxWidth == null ? undefined : CLIP;

// Welche Zeile unter dem Zeiger liegt, entscheidet .erp-row:hover in tokens/base.css
// und kein Zeilenindex im Zustand: Ein Index, der beim Nachladen unter dem stehenden
// Zeiger nicht zurückgesetzt wird, hebt danach die falsche Zeile hervor.
// Den Hoverwert bringt die Zeile selbst mit (--row-hot), weil er an ihrem Ton hängt;
// die Regel setzt daraus --row-hot-bg, und die klebenden Zellen erben es.
export function DataTable({ columns = [], rows = [], sort, onSort, onRowClick, stickyCount = 0, style = {} }) {
  return (
    <div className="nav-scroll" style={{ overflow: "auto", background: "var(--bg-table)", ...style }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-12)", whiteSpace: "nowrap" }}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={c.key} title={c.hint || c.label} onClick={() => onSort && onSort(c.key)} style={{
                position: "sticky", top: 0, left: i < stickyCount ? i * 34 : undefined, zIndex: i < stickyCount ? 4 : 2,
                padding: "7px 8px", textAlign: c.align || "left",
                width: c.width, minWidth: c.width,
                maxWidth: c.maxWidth,
                background: i < stickyCount ? "var(--bg-field)" : "var(--bg-menu)",
                borderBottom: "1px solid var(--line-e)",
                fontSize: "var(--fs-11)", fontWeight: "var(--fw-semibold)", color: "var(--fg-7)",
                userSelect: "none", cursor: onSort ? "pointer" : "default",
              }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, ...capStyle(c.maxWidth) }}>
                  <span style={clipStyle(c.maxWidth)}>{c.label}</span>
                  {sort && sort.key === c.key ? <Icon name={sort.dir === "asc" ? "chevron-up" : "chevron-down"} size={11} style={{ color: "var(--fg-4)", flexShrink: 0 }} /> : null}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => {
            const tone = r.tone ? "var(" + TONES[r.tone] + ")" : null;
            const tint = tone ? "color-mix(in srgb, " + tone + " 10%, transparent)" : "transparent";
            const hot = tone ? "color-mix(in srgb, " + tone + " 16%, transparent)" : "var(--hov-d)";
            const bg = "var(--row-hot-bg, " + tint + ")";
            return (
              <tr key={ri} className="erp-row" onClick={() => onRowClick && onRowClick(r, ri)}
                style={{ "--row-hot": hot, background: bg, cursor: onRowClick ? "pointer" : "default" }}>
                {columns.map((c, ci) => (
                  <td key={c.key} style={{
                    position: ci < stickyCount ? "sticky" : undefined, left: ci < stickyCount ? ci * 34 : undefined, zIndex: ci < stickyCount ? 3 : undefined,
                    padding: "4px 8px", textAlign: c.align || "left",
                    width: c.width, minWidth: c.width,
                    maxWidth: c.maxWidth,
                    background: ci < stickyCount ? "linear-gradient(" + bg + "," + bg + "), var(--bg-table)" : undefined,
                    borderBottom: "1px solid var(--line-g)",
                    color: ci === stickyCount ? "var(--fg-1)" : "var(--fg-4)",
                    fontVariantNumeric: c.align === "right" ? "tabular-nums" : undefined,
                  }}>{c.maxWidth == null ? r.cells[c.key] : <span style={{ display: "block", ...capStyle(c.maxWidth) }}>{r.cells[c.key]}</span>}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
